// Typed access to the gesture catalog (single source of truth:
// src/data/gestureCatalog.json — also read by Unity's CharacterAnimSetup at
// import time). A gesture's `id` is simultaneously the Animator state name,
// the bridge `emote` animation name, and the wire animState value.

import catalogJson from './gestureCatalog.json';

export interface GestureDef {
  id: string;
  file: string;
  label: string;
  emoji: string;
  category: GestureCategory;
  pickable?: boolean; // absent = true; false = locomotion-style, hidden from picker
  microIdle?: boolean;
}

export type GestureCategory =
  | 'greetings'
  | 'conversation'
  | 'reactions'
  | 'romance'
  | 'dance'
  | 'moods'
  | 'sitting'
  | 'actions'
  | 'locomotion';

export const GESTURE_CATEGORY_LABELS: { id: GestureCategory; label: string }[] = [
  { id: 'greetings', label: 'Greetings' },
  { id: 'conversation', label: 'Conversation' },
  { id: 'reactions', label: 'Reactions' },
  { id: 'romance', label: 'Romance' },
  { id: 'dance', label: 'Dances' },
  { id: 'moods', label: 'Moods' },
  { id: 'sitting', label: 'Sitting' },
  { id: 'actions', label: 'Actions' },
];

export const ALL_GESTURES: GestureDef[] = (catalogJson as { gestures: GestureDef[] }).gestures;

// Everything the user may put in their loadout (locomotion clips excluded).
export const PICKABLE_GESTURES: GestureDef[] = ALL_GESTURES.filter((g) => g.pickable !== false);

export const GESTURE_MAP: Map<string, GestureDef> = new Map(
  ALL_GESTURES.map((g) => [g.id, g])
);

// Loadout slots map to in-room hotkeys 1-8 and the emote bar, in order.
export const MAX_GESTURE_SLOTS = 8;

// Familiar legacy five first (old wire names still work), then new favorites.
export const DEFAULT_GESTURE_LOADOUT: string[] = [
  'wave', 'dance', 'cheer', 'sit', 'talk', 'blow-a-kiss', 'clapping', 'laughing',
];

/** Normalize a stored loadout: known ids only, pickable only, unique, capped. */
export function sanitizeGestureLoadout(loadout: unknown): string[] {
  if (!Array.isArray(loadout)) return [...DEFAULT_GESTURE_LOADOUT];
  const seen = new Set<string>();
  const clean: string[] = [];
  for (const id of loadout) {
    if (typeof id !== 'string' || seen.has(id)) continue;
    const def = GESTURE_MAP.get(id);
    if (!def || def.pickable === false) continue;
    seen.add(id);
    clean.push(id);
    if (clean.length >= MAX_GESTURE_SLOTS) break;
  }
  return clean.length > 0 ? clean : [...DEFAULT_GESTURE_LOADOUT];
}
